import type { ReactNode } from 'react'
import React from 'react'
import type { StackProps } from '@vtex/admin-ui'
import { Grid, Text, Button, csx } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'
import { useLocalization } from '@vtexlab/assisted-sales-localization'

import { FormattedPostalCode } from '../FormattedPostalCode'

export type ConfirmAddressInfo =
  | 'postalCode'
  | 'neighborhood'
  | 'street'
  | 'number'
  | 'complement'

type AddressRow = {
  id: ConfirmAddressInfo
  messageId: string
  value: string | ReactNode
  enabled: boolean
}

export type ConfirmAddressProps = StackProps & {
  onEditInfoClick?: (info: ConfirmAddressInfo) => void
  address: {
    postalCode: string
    number: string
    street?: string
    neighborhood?: string
    complement?: string
  }
}

export const ConfirmAddress = ({
  onEditInfoClick = () => {},
  address,
  ...props
}: ConfirmAddressProps) => {
  const { localizationConfig } = useLocalization()

  const addressRows: AddressRow[] = [
    {
      id: 'postalCode',
      messageId: 'checkout.confirm-cep-step.label',
      value: <FormattedPostalCode value={address.postalCode} />,
      enabled: localizationConfig.input.postalCode.enabled,
    },
    {
      id: 'street',
      messageId: 'checkout.home-delivery.step.confirm-address.street',
      value: address.street || '-',
      enabled: localizationConfig.input.street.enabled,
    },
    {
      id: 'number',
      messageId: 'checkout.home-delivery.step.address-number.label',
      value: address.number,
      enabled: localizationConfig.input.number.enabled,
    },
    {
      id: 'complement',
      messageId: 'checkout.home-delivery.step.address-complement.label',
      value: address.complement || '-',
      enabled: localizationConfig.input.complement.enabled,
    },
    {
      id: 'neighborhood',
      messageId: 'checkout.home-delivery.step.confirm-address.neighborhood',
      value: address.neighborhood || '-',
      enabled:
        localizationConfig.input.neighborhood.enabled &&
        localizationConfig.address.postalCodeMode !== 'state-neighborhood',
    },
  ]

  const handleInfoEditClick = (info: ConfirmAddressInfo) => {
    onEditInfoClick(info)
  }

  return (
    <Grid rowGap="1rem" {...props}>
      {addressRows.map((row) => (
        <div
          key={row.id}
          className={csx({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          })}
        >
          <Grid rowGap="0.5rem" data-testid={`address-${row.id}`}>
            <Text variant="title1" tone="secondary">
              <FormattedMessage id={row.messageId} />
            </Text>
            <Text variant="title1">{row.value}</Text>
          </Grid>
          <Button
            variant="tertiary"
            onClick={() => handleInfoEditClick(row.id)}
            data-testid={`address-confirm-change-${row.id}`}
          >
            <FormattedMessage id="change" />
          </Button>
        </div>
      ))}
    </Grid>
  )
}
